import React, { useMemo } from 'react';

import RecordBadge from './RecordBadge';
import { FilterValueLink } from './FilterValueTrigger';
import useEnsureCards from '../../hooks/useEnsureCards';
import { useCollectionIdentities } from '../../hooks/useCollectionIdentity';
import { useCollectionScope } from '../../contexts/CollectionScopeContext';
import { itemHomeCollectionId } from '../../utils/linkedRecords';
import { extractLookupIds } from '../../utils/lookups';

/**
 * A REFERENCE INTO ANOTHER COLLECTION reads as the record it is
 * (docs/platform-standards.md §1, "Qualify outside your collection"): the
 * qualified item badge — the collection's stacked tile and name, a divider,
 * then the card's tile with the card name over the record's title. A
 * reference inside the surface's own collection reads exactly as it always
 * has: its title, as text.
 *
 * What makes a reference foreign is the record it points at, which only a
 * POOL resolver knows (utils/lookups createLookupResolver `targetOf`): the
 * record's home (a linked row's source, else its `collection_id`) against the
 * surface's collection (contexts/CollectionScopeContext). A resolver without
 * a pool — the public page's title map — never qualifies anything, and
 * neither does a surface that turned qualification off. A reference whose
 * collection cannot be named yet (or ever) stays text.
 *
 *   const { qualified, localValue } = useQualifiedLookups(value, resolveLookupValue);
 *     qualified   [{ id, target, home, collection, card, title }] — the badges
 *     localValue  the value less those references (the same shape: an array
 *                 for allowMultiple, else one id or null), for the text
 */
export function useQualifiedLookups(value, resolveLookupValue) {
	const scope = useCollectionScope();
	const targetOf = typeof resolveLookupValue?.targetOf === 'function' ? resolveLookupValue.targetOf : null;
	const ids = extractLookupIds(value);
	const idsKey = ids.join(',');

	const candidates = useMemo(() => {
		if (!targetOf || scope.qualify === false) return [];
		return idsKey.split(',').filter(Boolean).map(id => {
			const target = targetOf(id);
			if (!target) return null;
			const home = itemHomeCollectionId(target);
			if (!home || home === scope.collectionId) return null;
			return { id, target, home };
		}).filter(Boolean);
	}, [idsKey, targetOf, scope]);

	const homes = useMemo(() => candidates.map(entry => entry.home), [candidates]);
	const identities = useCollectionIdentities(homes);
	const targets = useMemo(() => candidates.map(entry => entry.target), [candidates]);
	// A foreign record's card is often not in this session's cache yet.
	useEnsureCards(targets);

	return useMemo(() => {
		const qualified = candidates
			.filter(entry => identities.has(entry.home))
			.map(entry => ({
				...entry,
				collection: identities.get(entry.home),
				card: typeof resolveLookupValue?.cardOf === 'function' ? resolveLookupValue.cardOf(entry.id) : null,
				title: resolveLookupValue(entry.id) || '',
			}));
		if (!qualified.length) return { qualified, localValue: value };
		const taken = new Set(qualified.map(entry => entry.id));
		const rest = idsKey.split(',').filter(id => id && !taken.has(id));
		return { qualified, localValue: Array.isArray(value) ? rest : (rest[0] || null) };
	}, [candidates, identities, resolveLookupValue, value, idsKey]);
}

/** The qualified badges themselves — inert: a reference inside a list is the door to filtering by it, never a second link. */
export function QualifiedLookupBadges({ refs, size = 'xs' }) {
	return (refs || []).map(ref => (
		<RecordBadge
			key={ref.id}
			kind="item"
			record={ref.card}
			kicker={ref.card?.label?.singular || ref.card?.name || undefined}
			title={ref.title}
			collection={ref.collection}
			size={size}
			className="lookup-refs__badge"
		/>
	));
}

/**
 * A card-lookup value in a list cell or on a tile: the title text as ever
 * (`textOf`, the caller's own reading — the table and the tile print the
 * resolver's label), with any reference into another collection as its
 * qualified badge beside it. Inside a list the whole value stays the door to
 * "every record that points at this one" (FilterValueLink).
 */
export default function LookupRefs({ element, value, resolveLookupValue, size = 'xs', textOf = null, fallback = '—' }) {
	const { qualified, localValue } = useQualifiedLookups(value, resolveLookupValue);
	const readText = textOf || (raw => (resolveLookupValue ? resolveLookupValue(raw) : raw));
	if (!qualified.length) {
		return (
			<FilterValueLink element={element} value={value} resolveLookupValue={resolveLookupValue}>
				{readText(value) || fallback}
			</FilterValueLink>
		);
	}
	const hasLocal = Array.isArray(localValue) ? localValue.length > 0 : Boolean(localValue);
	const text = hasLocal ? readText(localValue) : '';
	return (
		<FilterValueLink element={element} value={value} resolveLookupValue={resolveLookupValue}>
			<span className="lookup-refs" data-testid="lookup-refs">
				{text ? <span className="lookup-refs__text">{text}</span> : null}
				<QualifiedLookupBadges refs={qualified} size={size} />
			</span>
		</FilterValueLink>
	);
}
